// SYNTHETIC placeholder rooms — prove engine mechanics in isolation (no real art).
// Two rooms wired by exits: A (west) <-> B (east). A->B is an edge-pan, B->A is a fade,
// so both transition styles are exercised.
import { loadRoom, type Room } from "../core/schema";
import type { QuestSpec } from "../engine/quest";

const W = 480;

// ---- Room A: "the longhouse" (L-shaped floor, pillar, an elder) ----
const roomA = {
  id: "sandbox_test",
  size: { w: W, h: 270 },
  background: { color: 0x14141c },
  spawn: { x: 90, y: 120 },
  actorRadius: 8,
  walkmesh: [
    [
      { x: 40, y: 60 },
      { x: 160, y: 60 },
      { x: 160, y: 250 },
      { x: 40, y: 250 },
    ],
    [
      { x: 160, y: 170 },
      { x: 440, y: 170 },
      { x: 440, y: 250 },
      { x: 160, y: 250 },
    ],
  ],
  hotspots: [
    {
      id: "well",
      label: "an old well",
      priority: 1,
      polygon: [
        { x: 70, y: 70 },
        { x: 130, y: 70 },
        { x: 130, y: 120 },
        { x: 70, y: 120 },
      ],
    },
  ],
  occluders: [{ id: "pillar", x: 300, y: 150, w: 34, h: 64, baselineY: 210, color: 0x3a3a52 }],
  npcs: [
    {
      id: "elder",
      x: 250,
      y: 215,
      radius: 9,
      color: 0x6e8b6e,
      label: "an elder",
      // wordless: a warm greeting, then points you toward the light beyond the door
      dialog: [
        { glyphs: ["greet"], mood: "warm" },
        { glyphs: ["light", "far", "door"], mood: "neutral" },
        { glyphs: ["help", "key", "question"], mood: "wary" },
      ],
    },
  ],
  exits: [
    {
      id: "east",
      target: "sandbox_east",
      spawn: { x: 60, y: 200 }, // entry point in room B
      transition: "edge-pan",
      pan: { x: W, y: 0 }, // B sits to the east
      polygon: [
        { x: 428, y: 170 },
        { x: 440, y: 170 },
        { x: 440, y: 250 },
        { x: 428, y: 250 },
      ],
    },
  ],
};

// ---- Room B: "the green ward" (wide floor, statue, a wanderer) ----
const roomB = {
  id: "sandbox_east",
  size: { w: W, h: 270 },
  background: { color: 0x101810 },
  spawn: { x: 60, y: 200 },
  actorRadius: 8,
  walkmesh: [
    [
      { x: 20, y: 150 },
      { x: 460, y: 150 },
      { x: 460, y: 250 },
      { x: 20, y: 250 },
    ],
  ],
  hotspots: [
    {
      id: "shrine",
      label: "a mossy shrine",
      priority: 1,
      polygon: [
        { x: 360, y: 90 },
        { x: 430, y: 90 },
        { x: 430, y: 150 },
        { x: 360, y: 150 },
      ],
    },
  ],
  occluders: [{ id: "statue", x: 200, y: 120, w: 28, h: 80, baselineY: 200, color: 0x2f3a2f }],
  npcs: [{ id: "wanderer", x: 320, y: 210, radius: 9, color: 0x8b7e6e, label: "a wanderer" }],
  exits: [
    {
      id: "west",
      target: "sandbox_test",
      spawn: { x: 410, y: 210 }, // back into room A's east wing
      transition: "fade",
      polygon: [
        { x: 20, y: 150 },
        { x: 34, y: 150 },
        { x: 34, y: 250 },
        { x: 20, y: 250 },
      ],
    },
    {
      id: "east",
      target: "sandbox_hall",
      spawn: { x: 80, y: 150 }, // entry into room C (top band)
      transition: "edge-pan",
      pan: { x: W, y: 0 },
      polygon: [
        { x: 446, y: 150 },
        { x: 460, y: 150 },
        { x: 460, y: 250 },
        { x: 446, y: 250 },
      ],
    },
  ],
};

// ---- Room C: "the hall" — a central TABLE obstacle (walkmesh hole). The floor is
// decomposed into 4 convex bands around the hole so the navmesh routes AROUND it. ----
const roomC = {
  id: "sandbox_hall",
  size: { w: W, h: 270 },
  background: { color: 0x181016 },
  spawn: { x: 80, y: 150 },
  actorRadius: 8,
  // floor [40,440] x [120,250] minus the table footprint hole [205,275] x [180,210]
  walkmesh: [
    [ { x: 40, y: 120 }, { x: 440, y: 120 }, { x: 440, y: 180 }, { x: 40, y: 180 } ], // top band
    [ { x: 40, y: 210 }, { x: 440, y: 210 }, { x: 440, y: 250 }, { x: 40, y: 250 } ], // bottom band
    [ { x: 40, y: 180 }, { x: 205, y: 180 }, { x: 205, y: 210 }, { x: 40, y: 210 } ], // left band
    [ { x: 275, y: 180 }, { x: 440, y: 180 }, { x: 440, y: 210 }, { x: 275, y: 210 } ], // right band
  ],
  hotspots: [],
  // table: visual is taller than its footprint (you can pass behind its top); the
  // footprint matches the walkmesh hole. baselineY = bottom of footprint.
  occluders: [{ id: "table", x: 205, y: 150, w: 70, h: 60, baselineY: 210, color: 0x4a3a2f }],
  npcs: [],
  exits: [
    {
      id: "west",
      target: "sandbox_east",
      spawn: { x: 430, y: 200 },
      transition: "fade",
      polygon: [
        { x: 40, y: 120 },
        { x: 54, y: 120 },
        { x: 54, y: 180 },
        { x: 40, y: 180 },
      ],
    },
    {
      id: "north",
      target: "sandbox_cloister",
      spawn: { x: 85, y: 200 },
      transition: "fade",
      polygon: [
        { x: 205, y: 120 },
        { x: 255, y: 120 },
        { x: 255, y: 138 },
        { x: 205, y: 138 },
      ],
    },
  ],
};

// ---- Room D: "the glade" — a chevron corridor with DIAGONAL walls. Two convex
// quads meet at an angled elbow; a straight line would leave the walkable area, so
// the funnel must bend the path around the inner corner. Tests diagonal walls +
// agent-radius on slanted edges + funnel smoothing. ----
const roomD = {
  id: "sandbox_glade",
  size: { w: W, h: 270 },
  background: { color: 0x0e1814 },
  spawn: { x: 100, y: 220 },
  actorRadius: 8,
  walkmesh: [
    [ { x: 60, y: 240 }, { x: 160, y: 240 }, { x: 220, y: 120 }, { x: 120, y: 120 } ], // lower arm (slants up-right)
    [ { x: 120, y: 120 }, { x: 220, y: 120 }, { x: 160, y: 30 }, { x: 60, y: 30 } ], // upper arm (slants up-left)
  ],
  hotspots: [],
  occluders: [],
  npcs: [],
  exits: [],
};

// ---- Room E: "the vale" — LARGER than the viewport (960x540 vs 480x270), so the
// camera must follow the hero and clamp at the room edges. ----
const roomE = {
  id: "sandbox_vale",
  size: { w: 960, h: 540 },
  background: { color: 0x0c1020 },
  spawn: { x: 100, y: 300 },
  actorRadius: 8,
  walkmesh: [[ { x: 40, y: 120 }, { x: 920, y: 120 }, { x: 920, y: 500 }, { x: 40, y: 500 } ]],
  hotspots: [
    {
      id: "farstone",
      label: "a far standing stone",
      priority: 1,
      polygon: [ { x: 840, y: 150 }, { x: 900, y: 150 }, { x: 900, y: 210 }, { x: 840, y: 210 } ],
    },
  ],
  occluders: [
    { id: "obelisk", x: 700, y: 230, w: 36, h: 100, baselineY: 330, color: 0x2a2f48 },
    { id: "cairn", x: 300, y: 360, w: 50, h: 40, baselineY: 400, color: 0x33384f },
  ],
  npcs: [{ id: "warden", x: 560, y: 420, radius: 9, color: 0x6e7b8b, label: "a warden" }],
  exits: [],
};

// ---- Room F: "the court" — a WANDERING NPC patrolling a diagonal beat. Tests a
// moving actor: stays on its mesh, separates from the hero (both movable), and
// re-sorts by depth live as it crosses the hero's feet-line. ----
const roomF = {
  id: "sandbox_court",
  size: { w: W, h: 270 },
  background: { color: 0x141810 },
  spawn: { x: 100, y: 200 },
  actorRadius: 8,
  walkmesh: [[ { x: 40, y: 90 }, { x: 440, y: 90 }, { x: 440, y: 250 }, { x: 40, y: 250 } ]],
  hotspots: [],
  occluders: [],
  npcs: [
    {
      id: "sentry",
      x: 180,
      y: 130,
      radius: 9,
      color: 0x8b6e6e,
      label: "a sentry",
      patrol: [ { x: 180, y: 130 }, { x: 300, y: 240 } ],
      speed: 50,
    },
  ],
  exits: [],
};

// ---- Room G: "the cell" — two interactables flanking the spawn + an npc, to prove
// the interact/armed-target primitive: proximity arming, nearest-pick, facing bias
// (the FRONT object arms even when slightly farther), and walk-up-then-talk. ----
const roomG = {
  id: "sandbox_interact",
  size: { w: W, h: 270 },
  background: { color: 0x12131a },
  spawn: { x: 240, y: 200 },
  actorRadius: 8,
  interactRadius: 60,
  walkmesh: [[ { x: 40, y: 120 }, { x: 440, y: 120 }, { x: 440, y: 250 }, { x: 40, y: 250 } ]],
  hotspots: [
    // satchel: behind the hero at spawn (to the left), and NEARER (anchor 35px away)
    { id: "satchel", verb: "take", interactAt: { x: 205, y: 200 },
      polygon: [ { x: 190, y: 165 }, { x: 220, y: 165 }, { x: 220, y: 195 }, { x: 190, y: 195 } ] },
    // brazier: in front of the hero at spawn (to the right), and FARTHER (anchor 45px away)
    { id: "brazier", verb: "use", interactAt: { x: 285, y: 200 },
      polygon: [ { x: 270, y: 160 }, { x: 300, y: 160 }, { x: 300, y: 195 }, { x: 270, y: 195 } ] },
  ],
  occluders: [],
  npcs: [{ id: "keeper", x: 390, y: 205, radius: 9, color: 0x6e7b8b, label: "the keeper" }],
  exits: [],
};

// ---- Room H: "the cloister" — a CONCAVE U-shape (two wings joined by a bottom bar).
// To cross from the left wing to the right wing the hero must route DOWN and around the
// bottom — never across the open top. A central idol occluder adds depth. The hub that
// links the main chain (hall) to the switchback and the crossing. ----
const roomH = {
  id: "sandbox_cloister",
  size: { w: W, h: 270 },
  background: { color: 0x161320 },
  spawn: { x: 85, y: 200 },
  actorRadius: 8,
  walkmesh: [
    [ { x: 40, y: 40 }, { x: 130, y: 40 }, { x: 130, y: 250 }, { x: 40, y: 250 } ], // left wing
    [ { x: 350, y: 40 }, { x: 440, y: 40 }, { x: 440, y: 250 }, { x: 350, y: 250 } ], // right wing
    [ { x: 130, y: 190 }, { x: 350, y: 190 }, { x: 350, y: 250 }, { x: 130, y: 250 } ], // bottom bar
  ],
  hotspots: [
    { id: "tapestry", verb: "examine", label: "a woven tapestry",
      polygon: [ { x: 50, y: 60 }, { x: 95, y: 60 }, { x: 95, y: 115 }, { x: 50, y: 115 } ] },
  ],
  occluders: [{ id: "idol", x: 230, y: 165, w: 30, h: 50, baselineY: 215, color: 0x3a3450 }],
  npcs: [{ id: "novice", x: 245, y: 225, radius: 9, color: 0x6e7b8b, label: "a novice" }],
  exits: [
    { id: "south", target: "sandbox_hall", spawn: { x: 230, y: 150 }, transition: "fade",
      polygon: [ { x: 55, y: 236 }, { x: 115, y: 236 }, { x: 115, y: 248 }, { x: 55, y: 248 } ] },
    { id: "switchback", target: "sandbox_switchback", spawn: { x: 70, y: 225 }, transition: "fade",
      polygon: [ { x: 365, y: 48 }, { x: 425, y: 48 }, { x: 425, y: 62 }, { x: 365, y: 62 } ] },
    { id: "crossing", target: "sandbox_crossing", spawn: { x: 240, y: 150 }, transition: "fade",
      polygon: [ { x: 365, y: 236 }, { x: 425, y: 236 }, { x: 425, y: 248 }, { x: 365, y: 248 } ] },
  ],
};

// ---- Room I: "the switchback" — a winding Z corridor (bottom -> riser -> top). Two
// right-angle bends force the funnel to string-pull around both inner corners; a good
// stress for multi-hop portal orientation. A lectern to examine at the far end. ----
const roomI = {
  id: "sandbox_switchback",
  size: { w: W, h: 270 },
  background: { color: 0x101a18 },
  spawn: { x: 70, y: 225 },
  actorRadius: 8,
  walkmesh: [
    [ { x: 40, y: 200 }, { x: 220, y: 200 }, { x: 220, y: 250 }, { x: 40, y: 250 } ], // bottom run
    [ { x: 170, y: 110 }, { x: 220, y: 110 }, { x: 220, y: 200 }, { x: 170, y: 200 } ], // riser
    [ { x: 170, y: 60 }, { x: 400, y: 60 }, { x: 400, y: 110 }, { x: 170, y: 110 } ], // top run
  ],
  hotspots: [
    { id: "lectern", verb: "use", label: "an old lectern",
      polygon: [ { x: 250, y: 62 }, { x: 290, y: 62 }, { x: 290, y: 98 }, { x: 250, y: 98 } ] },
  ],
  occluders: [],
  npcs: [],
  exits: [
    { id: "cloister", target: "sandbox_cloister", spawn: { x: 395, y: 80 }, transition: "fade",
      polygon: [ { x: 50, y: 236 }, { x: 100, y: 236 }, { x: 100, y: 248 }, { x: 50, y: 248 } ] },
    { id: "crossing", target: "sandbox_crossing", spawn: { x: 360, y: 150 }, transition: "fade",
      polygon: [ { x: 360, y: 68 }, { x: 398, y: 68 }, { x: 398, y: 104 }, { x: 360, y: 104 } ] },
  ],
};

// ---- Room J: "the crossing" — a CROSS/plus hub (centre + four arms). Multi-exit; the
// hero must route from arm to arm through the centre. A keeper stands in the middle and
// a milestone sits in the south arm. Links the cloister and the switchback into a loop. ----
const roomJ = {
  id: "sandbox_crossing",
  size: { w: W, h: 270 },
  background: { color: 0x141014 },
  spawn: { x: 240, y: 150 },
  actorRadius: 8,
  walkmesh: [
    [ { x: 200, y: 110 }, { x: 280, y: 110 }, { x: 280, y: 190 }, { x: 200, y: 190 } ], // centre
    [ { x: 210, y: 40 }, { x: 270, y: 40 }, { x: 270, y: 110 }, { x: 210, y: 110 } ], // north arm
    [ { x: 210, y: 190 }, { x: 270, y: 190 }, { x: 270, y: 250 }, { x: 210, y: 250 } ], // south arm
    [ { x: 40, y: 120 }, { x: 200, y: 120 }, { x: 200, y: 180 }, { x: 40, y: 180 } ], // west arm
    [ { x: 280, y: 120 }, { x: 440, y: 120 }, { x: 440, y: 180 }, { x: 280, y: 180 } ], // east arm
  ],
  hotspots: [
    { id: "milestone", verb: "examine", label: "a mossy milestone",
      polygon: [ { x: 225, y: 200 }, { x: 255, y: 200 }, { x: 255, y: 230 }, { x: 225, y: 230 } ] },
    // sealed until the gate lever (in another room) was pulled — proves GLOBAL persistence
    { id: "reliquary", verb: "take", label: "a sealed reliquary", gatedBy: { key: "gate_lever_pulled", scope: "global" },
      polygon: [ { x: 50, y: 130 }, { x: 90, y: 130 }, { x: 90, y: 165 }, { x: 50, y: 165 } ] },
  ],
  occluders: [],
  npcs: [
    {
      id: "keeper",
      x: 245,
      y: 165,
      radius: 9,
      color: 0x8b7e6e,
      label: "the keeper of ways",
      dialog: [
        { glyphs: ["greet", "path"], mood: "glad" },
        { glyphs: ["follow", "star"], mood: "warm" },
      ],
    },
  ],
  exits: [
    { id: "cloister", target: "sandbox_cloister", spawn: { x: 395, y: 200 }, transition: "fade",
      polygon: [ { x: 45, y: 135 }, { x: 80, y: 135 }, { x: 80, y: 165 }, { x: 45, y: 165 } ] },
    { id: "switchback", target: "sandbox_switchback", spawn: { x: 300, y: 85 }, transition: "fade",
      polygon: [ { x: 400, y: 135 }, { x: 435, y: 135 }, { x: 435, y: 165 }, { x: 400, y: 165 } ] },
    { id: "gate", target: "sandbox_gate", spawn: { x: 90, y: 200 }, transition: "fade",
      polygon: [ { x: 225, y: 236 }, { x: 255, y: 236 }, { x: 255, y: 248 }, { x: 225, y: 248 } ] },
    { id: "hearthward", target: "sandbox_hearthward", spawn: { x: 90, y: 200 }, transition: "fade",
      polygon: [ { x: 225, y: 46 }, { x: 255, y: 46 }, { x: 255, y: 60 }, { x: 225, y: 60 } ] },
  ],
};

// ---- Room K: "the gate" — a PUZZLE room proving room state + gated tiles. A chasm
// splits the floor (left platform | gap | right platform). A lever on the left toggles
// `bridge_down`; the bridge is a GATED AREA folded into the navmesh only when down. The
// chest (gated hotspot) and the exit (gated) are unreachable/inert until the bridge is
// lowered. With the bridge up, clicking across the chasm walks the hero to the edge. ----
const roomK = {
  id: "sandbox_gate",
  size: { w: W, h: 270 },
  background: { color: 0x14110e },
  spawn: { x: 90, y: 200 },
  actorRadius: 8,
  state: { bridge_down: false },
  walkmesh: [
    [ { x: 40, y: 120 }, { x: 200, y: 120 }, { x: 200, y: 250 }, { x: 40, y: 250 } ], // left platform
    [ { x: 280, y: 120 }, { x: 440, y: 120 }, { x: 440, y: 250 }, { x: 280, y: 250 } ], // right platform
  ],
  gatedAreas: [
    { id: "bridge", gatedBy: { key: "bridge_down" },
      polygon: [ { x: 200, y: 180 }, { x: 280, y: 180 }, { x: 280, y: 220 }, { x: 200, y: 220 } ] },
  ],
  hotspots: [
    // toggles the room-local bridge AND permanently records (global) that it was pulled,
    // which unseals the reliquary over in the crossing — a cross-room, persistent puzzle.
    { id: "lever", verb: "use", label: "a lever", interactAt: { x: 140, y: 190 },
      effect: { toggle: ["bridge_down"], setGlobal: { gate_lever_pulled: true } },
      polygon: [ { x: 120, y: 130 }, { x: 160, y: 130 }, { x: 160, y: 170 }, { x: 120, y: 170 } ] },
    { id: "chest", verb: "take", label: "an old chest", gatedBy: { key: "bridge_down" },
      polygon: [ { x: 360, y: 140 }, { x: 405, y: 140 }, { x: 405, y: 180 }, { x: 360, y: 180 } ] },
  ],
  occluders: [{ id: "gatepost", x: 200, y: 150, w: 12, h: 70, baselineY: 220, color: 0x3a3026 }],
  npcs: [],
  exits: [
    { id: "out", target: "sandbox_crossing", spawn: { x: 240, y: 150 }, transition: "fade",
      gatedBy: { key: "bridge_down" },
      polygon: [ { x: 426, y: 180 }, { x: 440, y: 180 }, { x: 440, y: 240 }, { x: 426, y: 240 } ] },
  ],
};

// ---- Room L: "the hearthward" — the CARRY-AND-KINDLE primitive. A chasm of "the Grey"
// splits the floor; the bridge of greylight only forms when the hero's flame is bright
// enough (minFlame 3). The hero starts at flame 1; kindling each of the two cold hearths
// grows it by 1. Kindle both -> flame 3 -> the greylight bridges the gap -> cross to the
// far shrine. Proves: flame as world-state, the kindle verb, flame growth, minFlame gating. ----
const roomL = {
  id: "sandbox_hearthward",
  size: { w: W, h: 270 },
  background: { color: 0x0d0c12 },
  spawn: { x: 90, y: 200 },
  actorRadius: 8,
  state: { h1_lit: false, h2_lit: false },
  walkmesh: [
    [ { x: 40, y: 120 }, { x: 200, y: 120 }, { x: 200, y: 250 }, { x: 40, y: 250 } ], // hearth platform
    [ { x: 280, y: 120 }, { x: 440, y: 120 }, { x: 440, y: 250 }, { x: 280, y: 250 } ], // far ledge
  ],
  gatedAreas: [
    // the greylight bridge: forms only when the carried flame is bright enough to cross
    { id: "greylight", gatedBy: { minFlame: 3 },
      polygon: [ { x: 200, y: 180 }, { x: 280, y: 180 }, { x: 280, y: 220 }, { x: 200, y: 220 } ] },
  ],
  hotspots: [
    { id: "hearth1", verb: "kindle", label: "a cold hearth", interactAt: { x: 135, y: 185 },
      gatedBy: { key: "h1_lit", scope: "room", eq: false }, effect: { flame: 1, set: { h1_lit: true } },
      polygon: [ { x: 120, y: 140 }, { x: 150, y: 140 }, { x: 150, y: 172 }, { x: 120, y: 172 } ] },
    { id: "hearth2", verb: "kindle", label: "a cold hearth", interactAt: { x: 175, y: 185 },
      gatedBy: { key: "h2_lit", scope: "room", eq: false }, effect: { flame: 1, set: { h2_lit: true } },
      polygon: [ { x: 160, y: 140 }, { x: 190, y: 140 }, { x: 190, y: 172 }, { x: 160, y: 172 } ] },
    { id: "shrine", verb: "examine", label: "the far shrine",
      polygon: [ { x: 360, y: 140 }, { x: 405, y: 140 }, { x: 405, y: 180 }, { x: 360, y: 180 } ] },
  ],
  occluders: [{ id: "greypost", x: 236, y: 150, w: 8, h: 70, baselineY: 220, color: 0x2a2a33 }],
  npcs: [],
  exits: [
    { id: "back", target: "sandbox_crossing", spawn: { x: 240, y: 150 }, transition: "fade",
      polygon: [ { x: 46, y: 236 }, { x: 100, y: 236 }, { x: 100, y: 248 }, { x: 46, y: 248 } ] },
  ],
};

export const rooms: Record<string, Room> = {
  [roomL.id]: loadRoom(roomL),
  [roomA.id]: loadRoom(roomA),
  [roomB.id]: loadRoom(roomB),
  [roomC.id]: loadRoom(roomC),
  [roomD.id]: loadRoom(roomD),
  [roomE.id]: loadRoom(roomE),
  [roomF.id]: loadRoom(roomF),
  [roomG.id]: loadRoom(roomG),
  [roomH.id]: loadRoom(roomH),
  [roomI.id]: loadRoom(roomI),
  [roomJ.id]: loadRoom(roomJ),
  [roomK.id]: loadRoom(roomK),
};
export const startRoomId = roomA.id;
// the sandbox renders at a 480×270 viewport, scale 3 (unchanged — the oracle tests rely on it)
export const view = { w: 480, h: 270 };
export const scale = 3;

// Sandbox "quest": reach the gate room (start -> east -> hall -> cloister -> crossing ->
// gate) and pull the lever, which sets the global flag. Proves the PDG analyzer end-to-end.
export const quest: QuestSpec = {
  win: [{ key: "gate_lever_pulled", scope: "global" }],
  initialGlobal: { flame: 1 }, // the hero always sets out carrying a small flame
};
